// =============================================================================
// domain/accountComparison.ts — the brief the coach reads about your accounts.
// -----------------------------------------------------------------------------
// One week, every account, side by side: what each made, how the volume was
// split, and — the part that is actually the question — whether the trader
// BEHAVES differently on one account than another.
//
// ── 🚨 IT BUILDS A PROMPT, IT DOES NOT SEND ONE ──
//
// Pure text assembly, so the whole thing is testable and nothing here can spend
// money. Every call to the coach is billed and burns a capped run, so the thing
// that decides WHETHER to send lives in the UI, behind a button, and the thing
// that decides WHAT to send lives here.
//
// ── ⚠ THE CLOCK AND THE WINDOW ARE INJECTED (D26) ──
//
// `weekStart`/`weekEnd` are passed in. A brief that read `new Date()` would
// produce a different prompt each time it was built, which breaks both the cache
// key and every test that pins its content.
//
// ── 🚨 THE POINT OF THE BRIEF IS THE BEHAVIOUR SECTION, NOT THE TABLE ──
//
// A snapshot table of P&L per account is something the trader can already see on
// the Accounts tab. What they cannot see is that they take more revenge trades
// on the funded account than the sim, or that their best pattern only works on
// one of them. So adherence, the negative-behaviour counts and the per-account
// mistake tags all go in even when they are empty — an EMPTY one is a finding
// too, and dropping it would let the coach assume it was fine.
// =============================================================================

export interface ComparisonAccount {
  id: string;
  name: string;
  firm: string | null;
  accountType: string | null;
  status: string | null;
  /** The drawdown rule in dollars, or 0/null when the firm sets none. */
  maxDrawdown: number | null;
  maxDrawdownType: string | null;
  /** Dollars of cushion left, already computed by the card model. */
  drawdownRoom: number | null;
  /**
   * 🚨 WHAT DECIDES WHETHER A QUIET ACCOUNT IS A FINDING OR NOISE.
   *
   * An ACTIVE account that traded nothing this week is the most obvious
   * cross-account observation there is, and dropping it would hide it. A blown,
   * archived one that traded nothing is history — and on Josh's real book ten of
   * fourteen are exactly that, so including them filled the table with rows the
   * coach would have had to reason about and pay for.
   */
  archived: boolean;
}

export interface ComparisonTrade {
  accountId: string | null;
  date: string | null;
  netPnl: number | null;
  patternId: string | null;
  mistakeTags: readonly string[] | null;
  /** ⚠ `false` means REVIEWED-and-clean is unknowable; see `adherenceRate`. */
  annotated?: boolean | undefined;
  booleans?: Readonly<Record<string, boolean>> | undefined;
}

export interface ComparisonInput {
  accounts: readonly ComparisonAccount[];
  /** Already narrowed to the window and to trades that count. */
  trades: readonly ComparisonTrade[];
  weekStart: string;
  weekEnd: string;
  /** Pattern id → name, for the "top patterns" line. */
  patternNames: Readonly<Record<string, string>>;
}

/** The minimum this is worth asking about. */
export const COMPARISON_MIN_ACCOUNTS = 2;

const money = (n: number): string => {
  const v = Math.round(n * 100) / 100;
  const sign = v < 0 ? '-' : '';
  return `${sign}$${Math.abs(v).toLocaleString('en-US', {
    minimumFractionDigits: 2, maximumFractionDigits: 2,
  })}`;
};

const pct = (n: number): string => `${Math.round(n)}%`;

/**
 * Per-account figures, computed here rather than reused from the card model.
 *
 * ⚠ THE CARD MODEL IS ALL-TIME AND THIS IS ONE WEEK. Handing the coach an
 * all-time win rate under a heading that says "this week" is the exact class of
 * error D130 is about — and a model will not question a number it is given.
 */
interface AccountRow {
  account: ComparisonAccount;
  trades: ComparisonTrade[];
  count: number;
  winRate: number;
  totalPnl: number;
  expectancy: number;
  /** Infinity when there were no losers. Rendered as `∞`. */
  profitFactor: number;
  best: number | null;
  worst: number | null;
}

function rowFor(a: ComparisonAccount, all: readonly ComparisonTrade[]): AccountRow {
  const trades = all.filter((t) => t.accountId === a.id);
  const pnls = trades.map((t) => Number(t.netPnl) || 0);
  const wins = pnls.filter((p) => p > 0);
  const losses = pnls.filter((p) => p < 0);
  const total = pnls.reduce((s, p) => s + p, 0);
  const grossLoss = losses.reduce((s, p) => s + -p, 0);
  const grossWin = wins.reduce((s, p) => s + p, 0);
  return {
    account: a,
    trades,
    count: trades.length,
    // ⚠ Wins over ALL trades, scratches included in the denominator — the same
    // definition every other surface in this client uses.
    winRate: trades.length ? (wins.length / trades.length) * 100 : 0,
    totalPnl: total,
    expectancy: trades.length ? total / trades.length : 0,
    profitFactor: grossLoss > 0 ? grossWin / grossLoss : (grossWin > 0 ? Infinity : 0),
    best: pnls.length ? Math.max(...pnls) : null,
    worst: pnls.length ? Math.min(...pnls) : null,
  };
}

/** The share of REVIEWED trades without the flag. Null when none were reviewed. */
function adherence(trades: readonly ComparisonTrade[], flag: string): number | null {
  const reviewed = trades.filter((t) => t.annotated !== false);
  if (!reviewed.length) return null;
  return Math.round(
    (100 * reviewed.filter((t) => t.booleans?.[flag] !== true).length) / reviewed.length,
  );
}

function countFlag(trades: readonly ComparisonTrade[], flag: string): number {
  return trades.filter((t) => t.booleans?.[flag] === true).length;
}

/**
 * The whole brief, as markdown.
 *
 * ⚠ MARKDOWN ON PURPOSE. It goes to a language model, not to a screen, and a
 * table it can read as a table is worth more than prose. The screen shows the
 * ANSWER, not this.
 */
export function buildComparisonBrief(input: ComparisonInput): string {
  const { accounts, trades, weekStart, weekEnd, patternNames } = input;
  const lines: string[] = [];
  lines.push(`# Account comparison — ${weekStart} → ${weekEnd}`, '');

  if (accounts.length < COMPARISON_MIN_ACCOUNTS) {
    lines.push(
      `Only one account is configured (${accounts[0]?.name ?? '—'}). A comparison needs at least two.`,
      '',
    );
    return lines.join('\n');
  }

  const all = accounts.map((a) => rowFor(a, trades));
  /**
   * 🚨 A QUIET ACCOUNT STAYS ONLY WHILE IT IS STILL LIVE.
   *
   * Measured on Josh's real book: fourteen accounts, four traded this week, and
   * the other ten are blown-and-archived. Listing them all put ten dead rows in
   * the table and ten "0% of the week" bullets under it — noise the model would
   * have had to read past, and pay to read past.
   *
   * ⚠ But an ACTIVE account with nothing on it is kept, because that is a
   * finding: it is the trader quietly abandoning one of the accounts they are
   * still paying for, and nothing else in the brief would say so.
   */
  const rows = all.filter((r) => r.count > 0 || (!r.account.archived && r.account.status === 'active'));
  const droppedQuiet = all.length - rows.length;

  // ── the snapshot ──────────────────────────────────────────────────────────
  lines.push('## Performance snapshot', '');
  lines.push('| Account | Trades | Win rate | P&L | Expectancy | PF | Best | Worst |');
  lines.push('|---|---|---|---|---|---|---|---|');
  for (const r of rows) {
    if (!r.count) {
      lines.push(`| **${r.account.name}** | 0 | — | — | — | — | — | — |`);
      continue;
    }
    const pf = r.profitFactor === Infinity ? '∞' : r.profitFactor.toFixed(2);
    lines.push(
      `| **${r.account.name}** | ${r.count} | ${pct(r.winRate)} | ${money(r.totalPnl)} `
      + `| ${money(r.expectancy)} | ${pf} | ${money(r.best ?? 0)} | ${money(r.worst ?? 0)} |`,
    );
  }
  // ⚠ SAYS WHAT WAS LEFT OUT. A table that silently omits ten of fourteen
  // accounts is a table the coach will reason about as if it were complete.
  if (droppedQuiet) {
    lines.push('');
    lines.push(
      `_${droppedQuiet} closed or archived account${droppedQuiet === 1 ? '' : 's'} `
      + `traded nothing this week and ${droppedQuiet === 1 ? 'is' : 'are'} left out._`,
    );
  }
  lines.push('');

  // ── where the volume went ─────────────────────────────────────────────────
  const total = trades.length;
  if (total) {
    lines.push('## Where the volume went', '');
    for (const r of rows) {
      lines.push(
        `- **${r.account.name}**: ${r.count} trade${r.count === 1 ? '' : 's'} `
        + `(${pct((r.count / total) * 100)} of the week)`,
      );
    }
    lines.push('');
  }

  // ── the behaviour, per account ────────────────────────────────────────────
  lines.push('## Behaviour, account by account', '');
  for (const r of rows) {
    if (!r.count) continue;
    const a = r.account;
    const head = [a.name, a.firm, a.accountType, a.status].filter(Boolean).join(' · ');
    lines.push(`### ${head}`, '');

    const adh = (v: number | null) => (v == null ? '—' : `${v}%`);
    lines.push(
      `- **Adherence:** plan ${adh(adherence(r.trades, 'planNotFollowed'))}`
      + ` · A+ ${adh(adherence(r.trades, 'subAPlus'))}`
      + ` · stop honoured ${adh(adherence(r.trades, 'stopNotHonored'))}`,
    );
    lines.push(
      `- **Negative behaviours:** revenge ${countFlag(r.trades, 'revengeTrade')}`
      + ` · FOMO ${countFlag(r.trades, 'fomoTrade')}`
      + ` · overtraded ${countFlag(r.trades, 'overtraded')}`,
    );

    // top patterns by P&L
    const byPattern = new Map<string, { count: number; pnl: number }>();
    for (const t of r.trades) {
      if (!t.patternId) continue;
      const cur = byPattern.get(t.patternId) ?? { count: 0, pnl: 0 };
      cur.count += 1;
      cur.pnl += Number(t.netPnl) || 0;
      byPattern.set(t.patternId, cur);
    }
    const top = [...byPattern.entries()]
      .sort((x, y) => y[1].pnl - x[1].pnl)
      .slice(0, 3)
      .map(([id, v]) => `${patternNames[id] ?? '?'} (${v.count}t, ${money(v.pnl)})`);
    if (top.length) lines.push(`- **Top patterns by P&L:** ${top.join('; ')}`);

    // top mistake tags
    const tags = new Map<string, number>();
    for (const t of r.trades) {
      for (const tag of t.mistakeTags ?? []) tags.set(tag, (tags.get(tag) ?? 0) + 1);
    }
    const topTags = [...tags.entries()]
      .sort((x, y) => y[1] - x[1]).slice(0, 3)
      .map(([tag, n]) => `\`${tag}\` ×${n}`);
    if (topTags.length) lines.push(`- **Top mistake tags:** ${topTags.join(', ')}`);

    if (a.maxDrawdown && a.maxDrawdown > 0) {
      lines.push(
        `- **Drawdown rule:** max ${money(a.maxDrawdown)} (${a.maxDrawdownType ?? 'static'})`
        + (a.drawdownRoom != null ? `, ${money(a.drawdownRoom)} of room left` : ''),
      );
    }
    lines.push('');
  }

  // ── what to answer ────────────────────────────────────────────────────────
  // ⚠ SPECIFIC QUESTIONS, NOT "ANALYSE THIS". An open brief comes back as a
  // summary of the table the trader can already read.
  lines.push('## Answer these');
  lines.push('- Which account is doing best this week, and why — better setup selection, better sizing, or better discipline?');
  lines.push('- Where does my behaviour DIFFER across accounts? Am I taking more revenge or FOMO trades on one than another?');
  lines.push('- Do my best patterns work on all of them, or only some? If only some, what is different about the others?');
  lines.push('- Is the volume split right, or am I overtrading the worst account and undertrading the best?');
  lines.push('- Should I cut size or pause any account where adherence has slipped or the drawdown is tight?');
  lines.push('- One specific change to apply across ALL accounts next week.');
  lines.push('');
  return lines.join('\n');
}
